import './InternalSecondaryMobile.css';
// src/MaintenanceAnalytics.js
//
// Internal punctuality dashboard for the maintenance team.
// Renders under the /maintenance hub at /maintenance/analytics, backed by
// the Flask endpoint /api/getTechStartTimes. This is a React port of the
// standalone team-start-times.html page hosted on Heroku.
//
// Pills (single row): Avg First Upload · Avg Last Upload · Avg Time
// Between Units · % On Target. Filters: Technician, Building, Day of Week,
// Date Range (with Custom range start/end pickers), Exclude Outliers.
// Below the filters: a Daily Activity Log table and a sortable + searchable
// Technician Performance table.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// See note in MaintenanceHome.js — use the shared config so production
// builds never pick up the local-dev REACT_APP_API_URL=localhost:5051.
import API_BASE_URL from './config/api';

const TARGET_MIN = 9 * 60 + 15; // 9:15 AM target for first upload

// ----- shared utilities ----------------------------------------------------

const fmtNumber = (n) => Number(n).toLocaleString();
const splitAmPm = (t) => {
  const m = String(t || '').match(/^(.*) (AM|PM)$/i);
  return m ? [m[1], m[2]] : [t || '—', ''];
};
const todayISO = () => {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 10);
};
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 10);
};

function badgeForFirst(mins, isOutlier) {
  if (mins == null) return { cls: 'muted', label: '—' };
  if (isOutlier) return { cls: 'muted', label: 'Outlier' };
  if (mins <= TARGET_MIN) return { cls: 'green', label: 'On time' };
  if (mins <= TARGET_MIN + 15) return { cls: 'yellow', label: 'Slightly late' };
  if (mins <= TARGET_MIN + 30) return { cls: 'orange', label: 'Late' };
  return { cls: 'red', label: 'Very late' };
}

// Status sort order, lower = better. Used only for the "Avg Start Status"
// column on the tech table so sorting it is meaningful.
function statusRank(mins) {
  if (mins == null) return -1;
  if (mins <= TARGET_MIN) return 0;
  if (mins <= TARGET_MIN + 15) return 1;
  if (mins <= TARGET_MIN + 30) return 2;
  return 3;
}

// ----- color tokens (matching the original page) ---------------------------

const C = {
  navy: '#1F3A68',
  navySoft: '#2C4E84',
  ink: '#1a2236',
  inkSoft: '#4a5468',
  muted: '#7a8499',
  line: '#E6E8EC',
  bg: '#F4F7FB',
  card: '#ffffff',
  green: '#1c9550',
  greenSoft: '#e3f5ea',
  yellow: '#C68A1C',
  yellowSoft: '#fdf3df',
  orange: '#C76121',
  orangeSoft: '#fcecdd',
  red: '#B62525',
  redSoft: '#fbe2e2',
  shadow: '0 1px 3px rgba(20, 30, 60, .06), 0 8px 24px rgba(20, 30, 60, .04)',
};

const sharedFont = '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif';

// ----- subcomponents -------------------------------------------------------

function Pill({ label, value, valueCls = 'muted', valueSuffix, sub, progress }) {
  const [time, period] = splitAmPm(value);
  const cls = {
    muted: { color: C.inkSoft },
    green: { color: C.green },
    red: { color: C.red },
    yellow: { color: C.yellow },
    target: { color: C.navy },
  }[valueCls] || { color: C.ink };

  return (
    <div
      style={{
        background: C.card,
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        padding: '16px 18px 14px',
        boxShadow: C.shadow,
        position: 'relative',
        overflow: 'hidden',
        minHeight: 118,
      }}
    >
      <div
        style={{
          color: C.muted,
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          fontWeight: 700,
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 32,
          fontWeight: 700,
          lineHeight: 1.05,
          letterSpacing: '-0.02em',
          ...cls,
        }}
      >
        {period ? (
          <>
            {time}
            <span style={{ fontSize: 17, fontWeight: 600, color: C.inkSoft, marginLeft: 4 }}>
              {period}
            </span>
          </>
        ) : valueSuffix ? (
          <>
            {value}
            <span style={{ fontSize: 17, fontWeight: 600, color: C.inkSoft, marginLeft: 4 }}>
              {valueSuffix}
            </span>
          </>
        ) : (
          value
        )}
      </div>
      {progress && (
        <div
          style={{
            height: 6,
            background: '#eef1f6',
            borderRadius: 4,
            marginTop: 10,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${Math.min(100, progress.pct)}%`,
              background: progress.color,
              transition: 'width .35s ease',
            }}
          />
        </div>
      )}
      <div
        style={{
          fontSize: 12,
          color: C.muted,
          marginTop: 6,
          lineHeight: 1.35,
        }}
        dangerouslySetInnerHTML={{ __html: sub || '&nbsp;' }}
      />
    </div>
  );
}

function Badge({ cls, children }) {
  const map = {
    green: { bg: C.greenSoft, fg: C.green },
    yellow: { bg: C.yellowSoft, fg: C.yellow },
    orange: { bg: C.orangeSoft, fg: C.orange },
    red: { bg: C.redSoft, fg: C.red },
    muted: { bg: '#eef1f6', fg: C.muted },
  };
  const m = map[cls] || map.muted;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '3px 9px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
        background: m.bg,
        color: m.fg,
      }}
    >
      {children}
    </span>
  );
}

// ----- main component ------------------------------------------------------

const TECH_COLUMNS = [
  { key: 'name', label: 'Technician', defaultDir: 'asc', num: false },
  { key: 'days_worked', label: 'Days', defaultDir: 'desc', num: true },
  { key: 'total_uploads', label: 'Uploads', defaultDir: 'desc', num: true },
  { key: 'uploads_per_day', label: 'Avg/day', defaultDir: 'desc', num: true },
  { key: 'avg_first_min', label: 'Avg Start', defaultDir: 'desc', num: true },
  { key: 'avg_last_min', label: 'Avg Finish', defaultDir: 'desc', num: true },
  { key: 'avg_gap_min', label: 'Avg Gap', defaultDir: 'asc', num: true },
  { key: 'pct_on_target', label: 'On Target', defaultDir: 'desc', num: true },
  { key: 'status_rank', label: 'Avg Start Status', defaultDir: 'desc', num: true },
];

export default function MaintenanceAnalytics() {
  // ---- filter state ----
  const [technician, setTechnician] = useState('');
  const [buildingId, setBuildingId] = useState('');
  const [dayOfWeek, setDayOfWeek] = useState('');
  const [lookback, setLookback] = useState('7'); // or 'custom'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [excludeOutliers, setExcludeOutliers] = useState(true);

  // ---- table-local state ----
  const [techSort, setTechSort] = useState({ key: 'avg_first_min', dir: 'desc' });
  const [techSearch, setTechSearch] = useState('');

  // ---- data ----
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isCustom = lookback === 'custom';
  // The dropdown options are populated from the FIRST successful response,
  // not from every response, so that they don't shrink while the user is
  // mid-filter.
  const initializedRef = useRef(false);
  const [buildingOptions, setBuildingOptions] = useState([]);
  const [techOptions, setTechOptions] = useState([]);

  // ---- fetch ----
  const fetchData = useCallback(async () => {
    const params = new URLSearchParams({ exclude_outliers: excludeOutliers ? 'true' : 'false' });
    if (lookback === 'today') {
      // Send today's date as both ends so the API returns just today.
      const t = todayISO();
      params.set('start_date', t);
      params.set('end_date', t);
    } else if (lookback === 'yesterday') {
      const y = daysAgoISO(1);
      params.set('start_date', y);
      params.set('end_date', y);
    } else if (isCustom && startDate && endDate) {
      params.set('start_date', startDate);
      params.set('end_date', endDate);
    } else {
      params.set('lookback_days', isCustom ? '7' : lookback);
    }
    if (buildingId) params.set('building_id', buildingId);
    if (dayOfWeek) params.set('day_of_week', dayOfWeek);
    if (technician) params.set('technician', technician);

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/getTechStartTimes?${params}`, {
        credentials: 'omit',
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status}: ${txt.slice(0, 200)}`);
      }
      const json = await res.json();
      setData(json);
      if (!initializedRef.current) {
        setBuildingOptions(json.buildings || []);
        setTechOptions(json.technicians_list || []);
        initializedRef.current = true;
      } else {
        if (!buildingId) setBuildingOptions(json.buildings || []);
        if (!technician) setTechOptions(json.technicians_list || []);
      }
    } catch (e) {
      console.error('[MaintenanceAnalytics] fetch failed', e);
      setError(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [excludeOutliers, isCustom, startDate, endDate, lookback, buildingId, dayOfWeek, technician]);

  // Refetch whenever filters change, debounced so rapid keypresses on date
  // inputs don't fire a wall of requests.
  useEffect(() => {
    if (isCustom && (!startDate || !endDate)) return;
    const t = setTimeout(fetchData, 60);
    return () => clearTimeout(t);
  }, [fetchData, isCustom, startDate, endDate]);

  // ---- when user flips to "custom range" prefill sensible defaults ----
  useEffect(() => {
    if (isCustom) {
      if (!endDate) setEndDate(todayISO());
      if (!startDate) setStartDate(daysAgoISO(7));
    }
  }, [isCustom, startDate, endDate]);

  // ---- handlers ----
  const onDateChange = useCallback((which, value) => {
    if (which === 'start') setStartDate(value);
    else setEndDate(value);
  }, []);

  // Auto-swap if user inverts the dates.
  useEffect(() => {
    if (isCustom && startDate && endDate && endDate < startDate) {
      const a = startDate;
      setStartDate(endDate);
      setEndDate(a);
    }
  }, [isCustom, startDate, endDate]);

  const resetFilters = useCallback(() => {
    setTechnician('');
    setBuildingId('');
    setDayOfWeek('');
    setLookback('7');
    setStartDate('');
    setEndDate('');
    setExcludeOutliers(true);
    initializedRef.current = false;
    setTechSearch('');
    setTechSort({ key: 'avg_first_min', dir: 'desc' });
  }, []);

  const onClickTech = useCallback((name) => {
    setTechnician(name);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // ---- pill data ----
  const pills = data?.pills;
  const view = data?.view || 'building';
  const fa = data?.filters_applied || {};

  // ---- daily table data ----
  const daily = data?.daily || [];

  // ---- technicians table after sort + search ----
  const technicians = data?.technicians || [];
  const techsFiltered = useMemo(() => {
    const q = techSearch.trim().toLowerCase();
    if (!q) return technicians;
    return technicians.filter((t) => t.name.toLowerCase().includes(q));
  }, [technicians, techSearch]);
  const techsDisplay = useMemo(() => {
    const { key, dir } = techSort;
    const mult = dir === 'asc' ? 1 : -1;
    const cmpKey = (t) => (key === 'status_rank' ? statusRank(t.avg_first_min) : t[key]);
    const arr = techsFiltered.slice();
    arr.sort((a, b) => {
      const va = cmpKey(a);
      const vb = cmpKey(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'string') return va.localeCompare(vb, undefined, { sensitivity: 'base' }) * mult;
      return (va - vb) * mult;
    });
    return arr;
  }, [techsFiltered, techSort]);

  const sortByCol = useCallback(
    (k) => {
      const col = TECH_COLUMNS.find((c) => c.key === k);
      if (!col) return;
      setTechSort((cur) =>
        cur.key === k
          ? { key: k, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
          : { key: k, dir: col.defaultDir },
      );
    },
    [],
  );

  // ---- pills section ----
  const renderPills = () => {
    if (!pills || !pills.avg_first) {
      return (
        <>
          <Pill label="Avg First Upload" value="—" sub="No data in window" />
          <Pill label="Avg Last Upload" value="—" sub="No data in window" />
          <Pill label="Avg Time Between Units" value="—" sub="No data in window" />
          <Pill
            label="% On Target (≤ 9:15 AM)"
            value="—"
            valueCls="target"
            sub="No data in window"
          />
        </>
      );
    }
    const avgF = pills.avg_first;
    const avgCls = avgF.meets_target ? 'green' : 'red';
    const diff = avgF.diff_minutes;
    const diffPill =
      diff === 0
        ? ''
        : diff < 0
          ? `<span style="display:inline-block;padding:2px 8px;border-radius:999px;font-size:11px;font-weight:600;margin-left:0;background:${C.greenSoft};color:${C.green};">${Math.abs(diff)}m ahead of target</span>`
          : `<span style="display:inline-block;padding:2px 8px;border-radius:999px;font-size:11px;font-weight:600;margin-left:0;background:${C.redSoft};color:${C.red};">${diff}m behind target</span>`;
    const dayLabel = view === 'technician' ? 'days worked' : 'building-days';
    const avgL = pills.avg_last;
    const avgG = pills.avg_gap;
    const pct = pills.pct_on_target;
    const pctCls = pct >= 75 ? 'green' : pct >= 50 ? 'yellow' : 'red';
    const pctColor = pctCls === 'green' ? C.green : pctCls === 'yellow' ? C.yellow : C.red;

    return (
      <>
        <Pill
          label="Avg First Upload"
          value={avgF.time}
          valueCls={avgCls}
          sub={`${diffPill ? diffPill + '<br>' : ''}Across ${fmtNumber(pills.total_days)} ${dayLabel}`}
        />
        <Pill
          label="Avg Last Upload"
          value={avgL ? avgL.time : '—'}
          sub="How late the day runs"
        />
        <Pill
          label="Avg Time Between Units"
          value={avgG ? avgG.display : '—'}
          sub={
            avgG
              ? `Pace per unit • ${avgG.days_contributing} active day${avgG.days_contributing === 1 ? '' : 's'}`
              : 'Need ≥2 uploads in a day'
          }
        />
        <Pill
          label="% On Target (≤ 9:15 AM)"
          value={String(pct)}
          valueSuffix="%"
          valueCls={pctCls}
          progress={{ pct, color: pctColor }}
          sub={pills.total_outliers_excluded ? `${pills.total_outliers_excluded} outlier${pills.total_outliers_excluded === 1 ? '' : 's'} excluded` : ''}
        />
      </>
    );
  };

  // ---- daily table render ----
  const renderDailyTable = () => {
    if (loading && !data) return <div style={emptyStyle}><span style={spinnerStyle} /> Loading…</div>;
    if (error) return <div style={{ ...emptyStyle, color: C.red }}>Couldn't load data: {error}</div>;
    if (!daily.length) return <div style={emptyStyle}>No uploads in the selected window with these filters.</div>;

    const isTechView = view === 'technician';
    return (
      <div className="internal-data-scroll" role="region" aria-label="Scrollable data table" tabIndex={0} style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Date</th>
              <th style={{ ...thStyle, ...colDayStyle }}>Day</th>
              <th style={{ ...thStyle, ...colBuildingStyle }}>{isTechView ? 'Building(s)' : 'Building'}</th>
              <th style={thStyle}>First</th>
              <th style={thStyle}>Last</th>
              <th style={{ ...thStyle, ...colSpanStyle }}>Span</th>
              <th style={{ ...thStyle, ...thNumStyle }}>Uploads</th>
              <th style={{ ...thStyle, ...thNumStyle }}>Avg Gap</th>
              {!isTechView && <th style={thStyle}>First Tech</th>}
              <th style={thStyle}>Status</th>
            </tr>
          </thead>
          <tbody>
            {daily.map((d, i) => {
              const b = badgeForFirst(d.first_minutes, d.is_first_outlier);
              const bldgs = isTechView
                ? ((d.buildings_on_day && d.buildings_on_day.join(', ')) || d.building_name)
                : d.building_name;
              return (
                <tr key={`${d.date}-${d.building_id || d.technician}-${i}`} style={trStyle}>
                  <td style={tdStyle}>{d.date_display}</td>
                  <td style={{ ...tdStyle, ...colDayStyle, color: C.muted, fontSize: 12 }}>{d.weekday}</td>
                  <td style={{ ...tdStyle, ...colBuildingStyle }} title={bldgs}>{bldgs}</td>
                  <td style={{ ...tdStyle, ...colTimeStyle }}>{d.first_time}</td>
                  <td style={{ ...tdStyle, ...colTimeStyle }}>{d.last_time}</td>
                  <td style={{ ...tdStyle, ...colSpanStyle, color: C.inkSoft }}>{d.span_display || '—'}</td>
                  <td style={{ ...tdStyle, ...tdNumStyle }}>{d.upload_count}</td>
                  <td style={{ ...tdStyle, ...tdNumStyle }}>{d.avg_gap_display || '—'}</td>
                  {!isTechView && (
                    <td style={tdStyle}>
                      <button
                        type="button"
                        onClick={() => onClickTech(d.first_worker || '')}
                        style={techLinkBtnStyle}
                      >
                        {d.first_worker || '—'}
                      </button>
                    </td>
                  )}
                  <td style={tdStyle}><Badge cls={b.cls}>{b.label}</Badge></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ---- tech table render ----
  const renderTechTable = () => {
    if (loading && !data) return <div style={emptyStyle}><span style={spinnerStyle} /> Loading…</div>;
    if (!technicians.length) return <div style={emptyStyle}>No technicians in this window.</div>;
    if (!techsDisplay.length) return <div style={emptyStyle}>No technicians match “{techSearch}”.</div>;

    const arrow = techSort.dir === 'asc' ? '▲' : '▼';
    return (
      <div className="internal-data-scroll" role="region" aria-label="Scrollable data table" tabIndex={0} style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {TECH_COLUMNS.map((c) => {
                const isActive = c.key === techSort.key;
                return (
                  <th
                    key={c.key}
                    onClick={() => sortByCol(c.key)}
                    style={{
                      ...thStyle,
                      ...(c.num ? thNumStyle : {}),
                      cursor: 'pointer',
                      userSelect: 'none',
                      color: isActive ? C.navy : thStyle.color,
                    }}
                    title={`Sort by ${c.label}`}
                  >
                    {c.label}
                    <span
                      style={{
                        display: 'inline-block',
                        marginLeft: 4,
                        opacity: isActive ? 1 : 0.35,
                        fontSize: 10,
                        color: isActive ? C.navy : 'inherit',
                      }}
                    >
                      {isActive ? arrow : '↕'}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {techsDisplay.map((t) => {
              const b = badgeForFirst(t.avg_first_min, false);
              const pctTxt = t.pct_on_target == null ? '—' : `${t.pct_on_target}%`;
              const pctCls =
                t.pct_on_target == null ? 'muted'
                : t.pct_on_target >= 75 ? 'green'
                : t.pct_on_target >= 50 ? 'yellow'
                : 'red';
              return (
                <tr key={t.name} style={trStyle}>
                  <td style={tdStyle}>
                    <button type="button" onClick={() => onClickTech(t.name)} style={techLinkBtnStyle}>
                      {t.name}
                    </button>
                  </td>
                  <td style={{ ...tdStyle, ...tdNumStyle }}>{t.days_worked}</td>
                  <td style={{ ...tdStyle, ...tdNumStyle }}>{t.total_uploads}</td>
                  <td style={{ ...tdStyle, ...tdNumStyle }}>{t.uploads_per_day}</td>
                  <td style={{ ...tdStyle, ...colTimeStyle }}>{t.avg_first_time || '—'}</td>
                  <td style={{ ...tdStyle, ...colTimeStyle }}>{t.avg_last_time || '—'}</td>
                  <td style={{ ...tdStyle, ...tdNumStyle }}>{t.avg_gap_display || '—'}</td>
                  <td style={tdStyle}><Badge cls={pctCls}>{pctTxt}</Badge></td>
                  <td style={tdStyle}><Badge cls={b.cls}>{b.label}</Badge></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const windowLbl = fa.custom_range && fa.range_label
    ? fa.range_label
    : (fa.lookback_days ? `last ${fa.lookback_days}d` : '');

  return (
    <div style={{ background: C.bg, color: C.ink, fontFamily: sharedFont, fontSize: 14, minHeight: '100vh' }}>
      <div className="internal-secondary-page internal-analytics-page" style={{ maxWidth: 1320, margin: '0 auto', padding: '28px 24px 64px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ fontSize: 26, margin: '0 0 4px 0', color: C.navy, fontWeight: 700, letterSpacing: '-0.01em' }}>
              Field Team Performance
            </h1>
            <div style={{ color: C.muted, fontSize: 13 }}>
              Punctuality, pace, and per-technician productivity across every in-suite job.{' '}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, background: '#eef3fb', color: C.navy, fontWeight: 600, fontSize: 12 }}>
                Target: first upload ≤ 9:15 AM
              </span>
            </div>
          </div>
          <div style={{ fontSize: 12, color: C.muted, textAlign: 'right' }}>
            {data && <div>Generated {data.generated_at}</div>}
            {data && <div>{fmtNumber(data.total_rows_scanned)} rows scanned • {windowLbl}</div>}
          </div>
        </div>

        {/* Focus banner */}
        {fa.technician && (
          <div style={focusBannerStyle}>
            <span>
              Showing days where <strong style={{ color: C.navy, fontWeight: 700 }}>{fa.technician}</strong> uploaded. Pills reflect this technician&apos;s personal stats.
            </span>
            <span style={{ flex: '1 1 auto' }} />
            <button type="button" onClick={() => setTechnician('')} style={resetBtnStyle}>Clear technician</button>
          </div>
        )}

        {/* Pills */}
        <div className="internal-mobile-stack"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 14,
            marginBottom: 18,
          }}
        >
          {renderPills()}
        </div>

        {/* Filter bar */}
        <div className="analytics-mobile-filters" style={filtersStyle}>
          <div style={filterStyle}>
            <label style={labelStyle}>Technician</label>
            <select
              aria-label="Technician"
              value={technician}
              onChange={(e) => setTechnician(e.target.value)}
              style={selectStyle}
            >
              <option value="">All technicians</option>
              {techOptions.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div style={filterStyle}>
            <label style={labelStyle}>Building</label>
            <select
              aria-label="Building"
              value={buildingId}
              onChange={(e) => setBuildingId(e.target.value)}
              style={selectStyle}
            >
              <option value="">All active</option>
              {buildingOptions.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div style={filterStyle}>
            <label style={labelStyle}>Day of Week</label>
            <select
              aria-label="Day of week"
              value={dayOfWeek}
              onChange={(e) => setDayOfWeek(e.target.value)}
              style={selectStyle}
            >
              <option value="">All days</option>
              <option value="monday">Monday</option>
              <option value="tuesday">Tuesday</option>
              <option value="wednesday">Wednesday</option>
              <option value="thursday">Thursday</option>
              <option value="friday">Friday</option>
              <option value="saturday">Saturday</option>
              <option value="sunday">Sunday</option>
            </select>
          </div>
          <div style={filterStyle}>
            <label style={labelStyle}>Date Range</label>
            <select
              aria-label="Date range"
              value={lookback}
              onChange={(e) => setLookback(e.target.value)}
              style={selectStyle}
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="7">Last 7 days</option>
              <option value="14">Last 14 days</option>
              <option value="30">Last 30 days</option>
              <option value="60">Last 60 days</option>
              <option value="90">Last 90 days</option>
              <option value="custom">Custom range…</option>
            </select>
          </div>
          <label style={toggleStyle} title="Outliers: first uploads outside 5am–1pm, last uploads after 9pm.">
            <input
              type="checkbox"
              checked={excludeOutliers}
              onChange={(e) => setExcludeOutliers(e.target.checked)}
              style={{ accentColor: C.navy, width: 16, height: 16, cursor: 'pointer' }}
            />
            Exclude outliers
            <span style={{ fontSize: 11.5, color: C.muted }}>ⓘ</span>
          </label>
          <div style={{ flex: '1 1 auto' }} />
          <button type="button" style={resetBtnStyle} onClick={resetFilters}>Reset filters</button>

          {isCustom && (
            <div className="analytics-mobile-date-range" style={rangeGroupStyle}>
              <div style={subFilterStyle}>
                <label style={labelStyle} htmlFor="ma-start-date">From</label>
                <input
                  id="ma-start-date"
                  type="date"
                  value={startDate}
                  max={todayISO()}
                  onChange={(e) => onDateChange('start', e.target.value)}
                  style={dateInputStyle}
                />
              </div>
              <span style={{ color: C.muted, padding: '0 2px 9px', fontWeight: 600, alignSelf: 'end' }}>→</span>
              <div style={subFilterStyle}>
                <label style={labelStyle} htmlFor="ma-end-date">To</label>
                <input
                  id="ma-end-date"
                  type="date"
                  value={endDate}
                  max={todayISO()}
                  onChange={(e) => onDateChange('end', e.target.value)}
                  style={dateInputStyle}
                />
              </div>
            </div>
          )}
        </div>

        {/* Daily Activity Log */}
        <div style={tableCardStyle}>
          <div style={tableHeadStyle}>
            <h3 style={{ margin: 0, fontSize: 14, color: C.ink, fontWeight: 700 }}>
              Daily Activity Log {view === 'technician' ? '(by technician)' : '(by building)'}
            </h3>
            <div style={{ color: C.muted, fontSize: 12 }}>
              {daily.length === 0 ? 'No matching days' : `${daily.length} day${daily.length === 1 ? '' : 's'}`}
            </div>
          </div>
          {renderDailyTable()}
        </div>

        {/* Technician Performance */}
        <div style={tableCardStyle}>
          <div style={tableHeadStyle}>
            <h3 style={{ margin: 0, fontSize: 14, color: C.ink, fontWeight: 700 }}>Technician Performance</h3>
            <div style={{ color: C.muted, fontSize: 12 }}>
              {!technicians.length ? 'No technicians' :
                techsFiltered.length === technicians.length
                  ? `${technicians.length} tech${technicians.length === 1 ? '' : 's'}`
                  : `${techsFiltered.length} of ${technicians.length} tech${technicians.length === 1 ? '' : 's'}`
              }
            </div>
          </div>
          <div style={techToolbarStyle}>
            <div style={{ flex: '0 1 260px', position: 'relative' }}>
              <input
                type="search"
                value={techSearch}
                onChange={(e) => setTechSearch(e.target.value)}
                placeholder="Find technician…"
                autoComplete="off"
                style={searchInputStyle}
              />
              {techSearch && (
                <button
                  type="button"
                  onClick={() => setTechSearch('')}
                  style={searchClearStyle}
                  title="Clear"
                >
                  ×
                </button>
              )}
            </div>
            <span style={{ fontSize: 11.5, color: C.muted, marginLeft: 'auto' }}>
              Click any column header to sort
            </span>
          </div>
          {renderTechTable()}
        </div>
      </div>
    </div>
  );
}

// ----- styles --------------------------------------------------------------

const focusBannerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  background: '#eef3fb',
  border: '1px solid #d6e0f3',
  borderRadius: 12,
  padding: '12px 16px',
  marginBottom: 14,
  color: C.navy,
  fontSize: 13,
};

const filtersStyle = {
  background: C.card,
  border: `1px solid ${C.line}`,
  borderRadius: 12,
  padding: '14px 16px',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '14px 18px',
  alignItems: 'center',
  boxShadow: C.shadow,
  margin: '18px 0',
};

const filterStyle = { display: 'flex', flexDirection: 'column', gap: 4 };
const labelStyle = {
  fontSize: 11,
  textTransform: 'uppercase',
  color: C.muted,
  fontWeight: 600,
  letterSpacing: '.05em',
};
const selectStyle = {
  appearance: 'none',
  WebkitAppearance: 'none',
  background: C.card,
  border: `1px solid ${C.line}`,
  borderRadius: 8,
  padding: '7px 32px 7px 10px',
  fontSize: 14,
  color: C.ink,
  minWidth: 160,
  cursor: 'pointer',
  backgroundImage:
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='%237a8499' d='M6 8 0 0h12z'/></svg>\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10px center',
  backgroundSize: 10,
  fontFamily: 'inherit',
};
const dateInputStyle = {
  appearance: 'none',
  WebkitAppearance: 'none',
  background: C.card,
  border: `1px solid ${C.line}`,
  borderRadius: 8,
  padding: '7px 10px',
  fontSize: 14,
  color: C.ink,
  minWidth: 140,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const toggleStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  color: C.inkSoft,
  cursor: 'pointer',
  userSelect: 'none',
};

const resetBtnStyle = {
  fontSize: 12,
  padding: '6px 10px',
  border: `1px solid ${C.line}`,
  borderRadius: 8,
  background: C.card,
  color: C.inkSoft,
  cursor: 'pointer',
};

const rangeGroupStyle = {
  display: 'flex',
  flexDirection: 'row',
  gap: 12,
  alignItems: 'flex-end',
  flexWrap: 'nowrap',
  flex: '0 0 100%',
  paddingTop: 12,
  marginTop: 4,
  borderTop: `1px dashed ${C.line}`,
  order: 5,
};
const subFilterStyle = { display: 'flex', flexDirection: 'column', gap: 4 };

const tableCardStyle = {
  background: C.card,
  border: `1px solid ${C.line}`,
  borderRadius: 14,
  boxShadow: C.shadow,
  overflow: 'hidden',
  marginBottom: 22,
};
const tableHeadStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '14px 18px',
  borderBottom: `1px solid ${C.line}`,
  gap: 12,
  flexWrap: 'wrap',
};
const techToolbarStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 16px 12px',
  borderBottom: `1px solid ${C.line}`,
  background: '#fafbfd',
  flexWrap: 'wrap',
};
const searchInputStyle = {
  width: '100%',
  padding: '7px 30px 7px 30px',
  border: `1px solid ${C.line}`,
  borderRadius: 8,
  background: '#fff',
  fontSize: 13,
  color: C.ink,
  fontFamily: 'inherit',
  // The 🔍 icon and × clear button live as overlays via separate spans
};
const searchClearStyle = {
  position: 'absolute',
  right: 6,
  top: '50%',
  transform: 'translateY(-50%)',
  border: 'none',
  background: 'none',
  color: C.muted,
  cursor: 'pointer',
  fontSize: 16,
  lineHeight: 1,
  padding: '4px 6px',
  borderRadius: 4,
};

const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const thStyle = {
  padding: '10px 13px',
  textAlign: 'left',
  background: '#fafbfd',
  fontWeight: 600,
  color: C.inkSoft,
  fontSize: 11.5,
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  borderBottom: `1px solid ${C.line}`,
  whiteSpace: 'nowrap',
};
const thNumStyle = { textAlign: 'right' };
const tdStyle = {
  padding: '10px 13px',
  borderBottom: '1px solid #f1f2f6',
  verticalAlign: 'middle',
  fontSize: 13.5,
};
const tdNumStyle = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: C.inkSoft };
const colDayStyle = {};
const colBuildingStyle = { fontWeight: 500, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const colSpanStyle = {};
const colTimeStyle = { fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: C.ink, whiteSpace: 'nowrap' };
const trStyle = {};
const techLinkBtnStyle = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: C.navy,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  fontWeight: 500,
};

const emptyStyle = {
  textAlign: 'center',
  padding: '50px 16px',
  color: C.muted,
  fontSize: 14,
};
const spinnerStyle = {
  width: 22,
  height: 22,
  border: '2px solid #e1e6f0',
  borderTopColor: C.navy,
  borderRadius: '50%',
  display: 'inline-block',
  animation: 'spin 0.8s linear infinite',
  verticalAlign: 'middle',
  marginRight: 8,
};

// Inject the spinner keyframes once.
if (typeof document !== 'undefined' && !document.getElementById('maintenance-analytics-keyframes')) {
  const style = document.createElement('style');
  style.id = 'maintenance-analytics-keyframes';
  style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}
